import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Calculator,
  TrendingUp,
  MapPin,
  BarChart3,
  Building2,
  DollarSign,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { generateSalaryPageUrl, extractLocationContext, createOccupationSlug } from '@/lib/occupationUtils';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface MetricNavigationProps {
  occupation?: {
    title?: string;
    occ_title?: string;
    soc?: string;
    soc_code?: string;
    slug?: string;
  };
  location?: string;
  currentMetric?: 'salary' | 'trends' | 'best-states' | 'rankings' | 'calculator' | 'industry';
  className?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
}

const MetricNavigation: React.FC<MetricNavigationProps> = ({
  occupation,
  location,
  currentMetric,
  className,
  collapsible = false,
  defaultOpen = true,
}) => {
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  
  // Determine target location
  const targetLocation = location || extractLocationContext(currentLocation.pathname) || 'united-states';
  
  // Get occupation data
  const occupationTitle = occupation?.title || occupation?.occ_title || '';
  const socCode = occupation?.soc || occupation?.soc_code || '';
  const occupationSlug = occupation?.slug || (occupationTitle ? createOccupationSlug(occupationTitle) : '');
  
  // Generate URLs for different metrics
  const salaryUrl = occupationSlug && occupationTitle
    ? generateSalaryPageUrl({ ...occupation, soc: socCode, soc_code: socCode }, targetLocation)
    : '/';
  
  const trendsUrl = occupationSlug && occupationTitle
    ? `/trends/${occupationSlug}/${targetLocation}/${socCode ? `?soc=${socCode}` : ''}`
    : '/trends/';
  
  const bestStatesUrl = occupationSlug
    ? `/best-states/${occupationSlug}/${socCode ? `?soc=${socCode}` : ''}`
    : '/best-states/';
  
  const rankingsUrl = socCode
    ? `/rankings/?soc=${socCode.substring(0, 2)}-`
    : '/rankings/';
  
  const calculatorUrl = occupationSlug && occupationTitle
    ? `/calculators/salary/?occupation=${encodeURIComponent(occupationTitle)}&soc=${socCode}`
    : '/calculators/salary/';
  
  const industryUrl = occupationSlug
    ? `/industry/?occupation=${occupationSlug}${socCode ? `&soc=${socCode}` : ''}`
    : '/industry/';
  
  const metrics = [
    {
      id: 'salary',
      label: 'Salary Details',
      description: occupationTitle ? `View ${occupationTitle} salary data` : 'View comprehensive salary data',
      icon: DollarSign,
      url: salaryUrl,
      available: !!occupationSlug && !!occupationTitle,
    },
    {
      id: 'trends',
      label: 'Salary Trends',
      description: occupationTitle ? `Historical wage analysis for ${occupationTitle}` : 'Historical wage analysis',
      icon: TrendingUp,
      url: trendsUrl,
      available: !!occupationSlug && !!occupationTitle,
    },
    {
      id: 'best-states',
      label: 'Best States',
      description: occupationTitle ? `Compare ${occupationTitle} across states` : 'Compare salaries by location',
      icon: MapPin,
      url: bestStatesUrl,
      available: !!occupationSlug,
    },
    {
      id: 'rankings',
      label: 'Occupation Rankings',
      description: socCode ? `See how this occupation ranks (SOC: ${socCode})` : 'See how occupations rank',
      icon: BarChart3,
      url: rankingsUrl,
      available: true,
    },
    {
      id: 'calculator',
      label: 'Salary Calculator',
      description: occupationTitle ? `Calculate wages for ${occupationTitle}` : 'Convert hourly to annual wages',
      icon: Calculator,
      url: calculatorUrl,
      available: true,
    },
    {
      id: 'industry',
      label: 'Industry Data',
      description: occupationTitle ? `View ${occupationTitle} industry breakdown` : 'View industry breakdown',
      icon: Building2,
      url: industryUrl,
      available: !!occupationSlug,
    },
  ];
  
  const availableMetrics = metrics.filter(m => m.available);
  const currentContext = occupationTitle
    ? `Viewing: ${occupationTitle}${socCode ? ` (SOC: ${socCode})` : ''}`
    : 'Explore Metrics';
  
  const content = (
    <div className="space-y-3">
      {collapsible && occupationTitle && (
        <div className="text-sm text-muted-foreground mb-2 break-words">
          {currentContext}
        </div>
      )}
      <div className="space-y-2">
        {availableMetrics.map((metric) => {
          const Icon = metric.icon;
          const isActive = currentMetric === metric.id;
          
          return (
            <Button
              key={metric.id}
              variant={isActive ? 'default' : 'outline'}
              className={cn(
                'w-full justify-start text-left h-auto py-3 min-w-0 whitespace-normal',
                isActive && 'bg-primary text-primary-foreground'
              )}
              onClick={() => navigate(metric.url)}
            >
              <Icon className="mr-2 h-4 w-4 flex-shrink-0 mt-0.5 self-start" />
              <div className="flex-1 text-left min-w-0">
                <div className="font-medium break-words">{metric.label}</div>
                <div className={cn(
                  'text-xs mt-0.5 break-words',
                  isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'
                )}>
                  {metric.description}
                </div>
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );
  
  if (collapsible) {
    return (
      <Card className={cn('w-full', className)}>
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Related Metrics</CardTitle>
                {isOpen ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {content}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  }
  
  return (
    <Card className={cn('w-full', className)}>
      <CardHeader>
        <CardTitle className="text-lg">Related Metrics</CardTitle>
        {occupationTitle && (
          <p className="text-sm text-muted-foreground mt-1 break-words">
            {currentContext}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
};

export default MetricNavigation;
